/**
 * Keyed-memoization helper for parametrized selector/hook factories.
 *
 * A factory like `configType => createSelector(...)` builds a fresh
 * selector (or hook) instance on every call. That's fine for the value it
 * computes, but `useSelector` relies on the *selector function itself*
 * staying referentially stable across renders — handing it a fresh
 * instance each render defeats its memoization and can cause render loops.
 * `memoizeByKey` gives any single-argument factory a cache so the same key
 * always returns the same built instance.
 *
 * Convention: parametrized selector/hook factories that return
 * `createSelector` instances (or hooks wrapping them) must be wrapped in
 * `memoizeByKey`; plain property-read closures (e.g. `id => state =>
 * state.foo[id]`) carry no referential-stability risk and may stay
 * uncached — say so in a comment at the call site.
 *
 * Contains:
 * - memoizeByKey — wraps `buildFn` with a cache keyed by `keyFor(key)`
 */

export const memoizeByKey = (buildFn, keyFor = String) => {
  const cache = new Map();
  return key => {
    const cacheKey = keyFor(key);
    if (!cache.has(cacheKey)) {
      cache.set(cacheKey, buildFn(key));
    }
    return cache.get(cacheKey);
  };
};
